import { Color, ColorMode } from '@color';
import {
  expectDefined,
  fixedNumber,
  produce,
  produceWithPatches,
  type Draft
} from '@utils';
import { LayoutElement } from './LayoutElement';
import { SnorfLayout } from './SnorfLayout';
import type { ElementId } from './types';

const PREFIX = '🟦 layout';

/**
 * Helper function that creates a transition function for entity layouts using immer
 * @param start The starting layout
 * @param end The ending layout
 * @param colorMode Color mode for realizing adaptive colors
 * @returns A function that takes a transition parameter t (0.0 to 1.0) and returns a SnorfLayout
 */
export function interpLayout(
  start: SnorfLayout,
  end: SnorfLayout,
  colorMode: ColorMode,
  opts?: { logging?: boolean }
): (t: number) => SnorfLayout {
  const { logging } = { logging: false, ...opts };

  // Type for layout mutator functions that work with immer Drafts
  interface Mutator {
    f: (draft: Draft<SnorfLayout>, t: number) => void;
    message: string;
  }
  const mutators: Mutator[] = [];

  function addMutator(mutator: Mutator): void {
    mutators.push(mutator);
    if (logging) {
      console.log(`${PREFIX} add: ${mutator.message}`);
    }
  }

  // Pre-populate working layout with sibling elements for content changes
  // This is done once during setup to avoid creating elements during animation
  let workingLayout = start;

  // Walk through the start layout tree to find differences and create mutators
  walkLayoutTree(start, (startElement: LayoutElement) => {
    const elementId = startElement.id;

    // Try to find corresponding element in endLayout
    const endElement = end.layoutElement(elementId);
    if (!endElement) {
      // Create a mutator to fade it out
      addMutator({
        message: `fade-out '${elementId}'`,
        f: (draft, t) => {
          const workElement = draft.getLayoutElement(elementId);
          workElement.opacity = 1 - t; // Fade out over time
        }
      });

      // Don't process children of exiting elements
      return { stop: true };
    }

    // Check if background colors are different
    const startBgColor =
      startElement.backgroundColor?.realize(colorMode) ?? Color.TRANSPARENT;
    const endBgColor =
      endElement.backgroundColor?.realize(colorMode) ?? Color.TRANSPARENT;

    if (!startBgColor.equals(endBgColor)) {
      // Create a mutator for this element's background color
      const colorInterp = startBgColor.interp(endBgColor);
      addMutator({
        message: `change background '${elementId}'`,
        f: (draft: Draft<SnorfLayout>, t: number) => {
          const workElement = draft.getLayoutElement(elementId);
          const interpolatedColor = colorInterp(t);
          workElement.backgroundColor = interpolatedColor;
        }
      });
    }

    // Check if bounds are different
    const startBounds = startElement.bounds;
    const endBounds = endElement.bounds;
    let needsSiblingForContentChange = false;

    // Handle text content changes
    if (startElement.isText && endElement.isText) {
      const startText = expectDefined(startElement.textData);
      const endText = expectDefined(endElement.textData);

      // Check if the texts are different by comparing their paragraph IDs
      const startParagraphId = expectDefined(startText.measured).paragraphId;
      const endParagraphId = expectDefined(endText.measured).paragraphId;

      if (startParagraphId !== endParagraphId) {
        needsSiblingForContentChange = true;

        // Create sibling element for cross-fade
        const siblingId = `${elementId}-end` as ElementId;

        // Use produce to create sibling element with endElement content
        workingLayout = produce(workingLayout, (draft: Draft<SnorfLayout>) => {
          // Create sibling by copying endElement structure
          const endElementCopy = produce(
            endElement,
            (elemDraft: Draft<LayoutElement>) => {
              elemDraft.setIdUnchecked(siblingId);
              elemDraft.opacity = 0; // Start invisible
              elemDraft.parentId = startElement.parentId;
              elemDraft.childIds = []; // Siblings don't need children for content changes
            }
          );

          // Add sibling to the layout
          draft.addLayoutElement(endElementCopy);

          // Add sibling to parent's children if parent exists
          if (startElement.parentId) {
            const parent = draft.getLayoutElement(startElement.parentId);
            parent.childIds.push(siblingId);
          }
        });

        // Create mutator for text cross-fade
        addMutator({
          message: `cross-fade text '${elementId}'`,
          f: (draft: Draft<SnorfLayout>, t: number) => {
            const workElement = draft.getLayoutElement(elementId);
            const sibling = draft.getLayoutElement(siblingId);

            workElement.opacity = 1 - t; // Fade out start text
            sibling.opacity = t; // Fade in end text
          }
        });
      }
    }

    // Handle image content changes
    if (startElement.isImage && endElement.isImage) {
      const startImage = expectDefined(startElement.imageData);
      const endImage = expectDefined(endElement.imageData);

      // Check if the images are different by comparing their source IDs
      const startImageId = startImage.source.id;
      const endImageId = endImage.source.id;

      if (startImageId !== endImageId) {
        needsSiblingForContentChange = true;

        // Create sibling element for cross-fade
        const siblingId = `${elementId}-end` as ElementId;

        // Use produce to create sibling element with endElement content
        workingLayout = produce(workingLayout, (draft: Draft<SnorfLayout>) => {
          // Create sibling by copying endElement structure
          const endElementCopy = produce(
            endElement,
            (elemDraft: Draft<LayoutElement>) => {
              elemDraft.setIdUnchecked(siblingId);
              elemDraft.opacity = 0; // Start invisible
              elemDraft.parentId = startElement.parentId;
              elemDraft.childIds = []; // Siblings don't need children for content changes
            }
          );

          // Add sibling to the layout
          draft.addLayoutElement(endElementCopy);

          // Add sibling to parent's children if parent exists
          if (startElement.parentId) {
            const parent = draft.getLayoutElement(startElement.parentId);
            parent.childIds.push(siblingId);
          }
        });

        // Create mutator for image cross-fade
        addMutator({
          message: `cross-fade image '${elementId}'`,
          f: (draft: Draft<SnorfLayout>, t: number) => {
            const workElement = draft.getLayoutElement(elementId);
            const sibling = draft.getLayoutElement(siblingId);

            workElement.opacity = 1 - t; // Fade out start image
            sibling.opacity = t; // Fade in end image
          }
        });
      }
    }

    // Handle bounds changes
    if (!startBounds.equals(endBounds)) {
      const boundsInterp = startBounds.interp(endBounds);

      if (needsSiblingForContentChange) {
        // Update both main element and sibling bounds
        const siblingId = `${elementId}-end` as ElementId;
        addMutator({
          message: `cross-fade image '${elementId}'`,
          f: (draft: Draft<SnorfLayout>, t: number) => {
            const interpolatedBounds = boundsInterp(t);
            const workElement = draft.getLayoutElement(elementId);
            const sibling = draft.getLayoutElement(siblingId);

            workElement.bounds = interpolatedBounds;
            sibling.bounds = interpolatedBounds; // Sibling should have same bounds
          }
        });
      } else {
        // Update only main element bounds
        addMutator({
          message: `update bounds '${elementId}'`,
          f: (draft: Draft<SnorfLayout>, t: number) => {
            const workElement = draft.getLayoutElement(elementId);
            const interpolatedBounds = boundsInterp(t);
            workElement.bounds = interpolatedBounds;
          }
        });
      }
    }

    return { stop: needsSiblingForContentChange };
  });

  // Walk the endLayout to find new elements that need to be added
  walkLayoutTree(end, (endElement: LayoutElement) => {
    const elementId = endElement.id;

    // Check if this element exists in startLayout
    const startElement = start.layoutElement(elementId);
    if (startElement) {
      // Element exists in both layouts, continue to children
      return { stop: false };
    }

    // Element doesn't exist in startLayout, so it needs to be added
    // Pre-populate working layout with the new element (invisible initially)
    workingLayout = produce(workingLayout, (draft: Draft<SnorfLayout>) => {
      // Use produce to create new element with proper structure
      const newElement = produce(endElement, (elemDraft: Draft<LayoutElement>) => {
        elemDraft.opacity = 0; // Start invisible
      });

      // Add element to the layout
      draft.addLayoutElement(newElement);

      // Add to parent's children if parent exists
      if (newElement.parentId) {
        const parent = draft.layoutElement(newElement.parentId);
        if (parent) {
          parent.childIds.push(elementId);
        }
      }

      function addChildren(parentId: ElementId): void {
        const parent = end.getLayoutElement(parentId);

        for (const childId of parent.childIds) {
          const child = end.getLayoutElement(childId);
          draft.addLayoutElement(child);
          if (logging) {
            console.log(`${PREFIX} add child: ${child.id}`);
          }
          addChildren(child.id);
        }
      }

      // Recursively add children
      addChildren(elementId);
    });

    // Create a mutator to fade it in
    addMutator({
      message: `fade-in '${elementId}'`,
      f: (draft: Draft<SnorfLayout>, t: number) => {
        const newElement = draft.getLayoutElement(elementId);
        newElement.opacity = t; // Fade in over time
      }
    });

    // Stop descending into children since we handle the entire subtree when adding
    return { stop: true };
  });

  // Return the immer-based transition function
  return (t: number): SnorfLayout => {
    if (logging) {
      console.log(`${PREFIX} at t=${fixedNumber(t, 2)}`);
    }
    if (t <= 0) {
      if (logging) console.log(`${PREFIX} returning start state`);
      return start;
    }
    if (t >= 1) {
      if (logging) console.log(`${PREFIX} returning end state`);
      return end;
    }

    let nextState: SnorfLayout;
    if (logging) {
      const [_nextState, patches] = produceWithPatches(workingLayout, draft => {
        for (const mutator of mutators) {
          console.log(`${PREFIX} run: ${mutator.message}`);
          mutator.f(draft, t);
        }
      });
      console.log(`${PREFIX} patches:`, patches);
      nextState = _nextState;
    } else {
      nextState = produce(workingLayout, draft => {
        for (const mutator of mutators) {
          mutator.f(draft, t);
        }
      });
    }
    return nextState;
  };
}

/**
 * Visitor function for walkLayoutTree
 */
export type LayoutVisitor = (element: LayoutElement) => {
  newSibling?: LayoutElement;
  stop: boolean;
};

/**
 * Performs a breadth-first walk of the SnorfLayout tree, including ROOT_ID.
 * It visits all elements once depth-first, calling visitor for each element.
 * @param layout The layout to traverse
 * @param visitor The visitor function called for each element
 */
export function walkLayoutTree(layout: SnorfLayout, visitor: LayoutVisitor): void {
  const visited = new Set<ElementId>();
  const queue: LayoutElement[] = [];

  // Start with the root element
  const rootElement = layout.getLayoutElement(SnorfLayout.ROOT_ID);
  queue.push(rootElement);

  while (queue.length > 0) {
    const element = expectDefined(queue.shift());

    // Skip if already visited
    if (visited.has(element.id)) {
      continue;
    }
    visited.add(element.id);

    // Visit the element
    const result = visitor(element);

    // If the visitor returned a new sibling, add it to the element's parent
    if (result.newSibling) {
      if (element.parentId) {
        const parent = layout.getLayoutElement(element.parentId);
        result.newSibling.parentId = parent.id;
        parent.addChild(result.newSibling.id);
        layout.addLayoutElement(result.newSibling);
      }
    }

    // If not stopping, add children to the queue
    if (!result.stop) {
      // Add children to the queue for further processing
      for (const childId of element.childIds) {
        const child = layout.getLayoutElement(childId);
        queue.push(child);
      }
    }
  }
}
